import { useEffect, useState } from "react";
import { FileText, Image as ImageIcon, FileSpreadsheet, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const iconFor = (ct = "") => {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.includes("spreadsheet") || ct.includes("excel") || ct.includes("csv")) return FileSpreadsheet;
  return FileText;
};

/**
 * Thumbnail for a library asset. Hits the thumb endpoint (cheap, cached) and
 * only falls back to the full file for images when no thumbnail exists.
 */
export const AssetThumb = ({ asset, authHeader, className = "", iconSize = 20 }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const Icon = iconFor(asset.content_type);
  const isImage = (asset.content_type || "").startsWith("image/");
  const ext = (asset.filename?.split(".").pop() || "file").toUpperCase().slice(0, 5);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      const urls = [`${API}/library/assets/${asset.id}/thumb`];
      if (isImage) urls.push(`${API}/library/assets/${asset.id}/download`);
      for (const url of urls) {
        try {
          const resp = await fetch(url, { headers: authHeader() });
          if (!resp.ok) continue;
          const b = await resp.blob();
          if (cancelled) return;
          objectUrl = URL.createObjectURL(b);
          setBlobUrl(objectUrl);
          return;
        } catch {
          /* fall through to the icon */
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, isImage, authHeader]);

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={asset.filename || "Asset preview"}
        loading="lazy"
        className={`object-cover ${className}`}
      />
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center gap-1 bg-linen/70 text-ink-400 ${className}`}>
      <Icon size={iconSize} />
      <span className="text-[9px] font-semibold tracking-widest">{ext}</span>
    </div>
  );
};

export default AssetThumb;
